import api from './api'
import { cacheOr, cacheInvalidate, invalidatePrefix, memClear } from './cacheService'

// Política: dados do banco são cacheados APENAS em memória (lsTTL: 0).
// Nenhum ID de banco vai para localStorage/cookie — somem ao recarregar,
// quando o Firebase reautentica e a cadeia é re-derivada a partir do uid.
// Voláteis (viagens/presença, logs, listas admin) não são cacheados.
const MEM = {
  USER:    5  * 60_000,
  PERSON:  10 * 60_000,
  STUDENT: 5  * 60_000,
  ROUTES:  10 * 60_000,
  ADDRESS: 10 * 60_000,
  NOTIF:   1  * 60_000,
}

// Helper: cache só em memória (lsTTL: 0) para entidades do banco.
function memCacheOr(key, fetchFn, memTTL, forceRefresh = false) {
  return cacheOr(key, fetchFn, { memTTL, lsTTL: 0, forceRefresh })
}

export async function loginOuCriarUsuario(firebaseUser) {
  const { data } = await api.post('/users/login', {
    firebaseUid: firebaseUser.uid,
    email:       firebaseUser.email,
  })
  import('./cacheService').then(({ memSet }) => {
    memSet(`user:${firebaseUser.uid}`, data, MEM.USER)
  })
  return data
}

export async function getUserData(firebaseUser, forceRefresh = false) {
  if (!firebaseUser?.uid) return null

  return memCacheOr(
    `user:${firebaseUser.uid}`,
    async () => {
      try {
        const { data } = await api.get(`/users/${firebaseUser.uid}`)
        return data
      } catch (error) {
        if (error.response?.status === 404) {
          try {
            return await loginOuCriarUsuario(firebaseUser)
          } catch (loginErr) {
            if (loginErr.response?.status === 409) {
              // Email já existe com outro UID — conflito de identidade, forçar logout
              const { getAuth, signOut } = await import('firebase/auth')
              await signOut(getAuth())
              window.location.href = '/login'
              return null
            }
            throw loginErr
          }
        }
        throw error
      }
    },
    MEM.USER,
    forceRefresh
  )
}

export async function getPerson(personId, forceRefresh = false) {
  if (!personId) return null

  return memCacheOr(
    `person:${personId}`,
    () => api.get(`/persons/${personId}`).then(r => r.data),
    MEM.PERSON,
    forceRefresh
  )
}

export async function getStudentByPerson(personId, forceRefresh = false) {
  if (!personId) return null

  return memCacheOr(
    `student:person:${personId}`,
    () => api.get(`/students/person/${personId}`).then(r => r.data),
    MEM.STUDENT,
    forceRefresh
  )
}

export async function getStudentRoutes(studentId, forceRefresh = false) {
  if (!studentId) return null

  return memCacheOr(
    `routes:student:${studentId}`,
    () => api.get(`/students/${studentId}/routes`).then(r => r.data),
    MEM.ROUTES,
    forceRefresh
  )
}

/**
 * Veículo designado ao aluno logado numa rota, se o Planejamento Diário já
 * tiver sido aprovado. Sem cache — status muda (gerar/ajustar/aprovar) sem
 * aviso, e a tela quer o dado sempre atual.
 */
export async function buscarMeuVeiculo(routeId) {
  const { data } = await api.get(`/student-trips/meu-veiculo/${routeId}`)
  return data
}

/**
 * Check-in via QR code (mesmo código pra todo mundo que embarca na viagem) —
 * o backend valida que o aluno logado tem presença confirmada nessa viagem
 * antes de marcar o check-in.
 */
export async function fazerCheckin(tripId) {
  const { data } = await api.patch('/student-trips/checkin', { tripId })
  return data
}

export async function getAddressesByPerson(personId, forceRefresh = false) {
  if (!personId) return null

  return memCacheOr(
    `address:person:${personId}`,
    () => api.get(`/addresses/person/${personId}`).then(r => r.data),
    MEM.ADDRESS,
    forceRefresh
  )
}

export async function getRoutes(forceRefresh = false) {
  return memCacheOr(
    `routes:all`,
    () => api.get('/routes').then(r => r.data),
    MEM.ROUTES,
    forceRefresh
  )
}

export async function getRoute(routeId, forceRefresh = false) {
  if (!routeId) return null

  return memCacheOr(
    `route:${routeId}`,
    () => api.get(`/routes/${routeId}`).then(r => r.data),
    MEM.ROUTES,
    forceRefresh
  )
}

export async function verificarStatusCadastro(firebaseUser) {
  // Deixa erros de rede propagarem para o guard tratar como "backend offline"
  const { data } = await api.get(`/users/${firebaseUser.uid}/registration-status`)
  return data.status
}

export async function isUserAdmin(firebaseUser) {
  if (!firebaseUser) return false
  try {
    const data = await getUserData(firebaseUser)
    const role = (data?.role || '').toLowerCase()
    return role === 'admin' || role === 'admin_master'
  } catch {
    return false
  }
}

export async function isUserDev(firebaseUser) {
  if (!firebaseUser) return false
  try {
    const data = await getUserData(firebaseUser)
    return (data?.role || '').toLowerCase() === 'dev'
  } catch {
    return false
  }
}

export async function cadastrarPerson(firebaseUser, personData) {
  const { data } = await api.post('/persons', personData, { _noRetry: true })
  return data
}

export async function cadastrarStudent(firebaseUser, studentData) {
  const { data } = await api.post('/students', studentData, { _noRetry: true })
  return data
}

export async function cadastrarAddress(firebaseUser, addressData) {
  const { data } = await api.post('/addresses', addressData, { _noRetry: true })
  return data
}

export async function getNotificacoes(firebaseUser, userId, forceRefresh = false) {
  return memCacheOr(
    `notifications:${userId}`,
    () => api.get(`/notifications/user/${userId}`).then(r => r.data),
    MEM.NOTIF,
    forceRefresh
  )
}

export async function getNotificacoesNaoLidas(firebaseUser, userId) {
  const { data } = await api.get(`/notifications/user/${userId}/unread`)
  return data
}

export async function marcarNotificacaoLida(firebaseUser, notificationId) {
  const { data } = await api.put(`/notifications/${notificationId}/read`, {})
  invalidatePrefix('notifications:')
  return data
}

export async function salvarNickname(firebaseUser, nickname) {
  const { data } = await api.patch(
    `/users/${firebaseUser.uid}/nickname`,
    { nickName: nickname }
  )
  clearUserCache(firebaseUser.uid)
  return data
}

export async function confirmarPresenca(tripId, direction) {
  const { data } = await api.patch('/student-trips/confirmar', { tripId, direction })
  invalidatePrefix('trips:')
  return data
}

export async function enviarNotificacaoBroadcast(payload) {
  const { data } = await api.post('/notifications/broadcast', payload)
  return data
}

export async function atualizarLembretesDeViagem(enabled) {
  const { data } = await api.patch('/students/me/trip-reminders', { enabled })
  return data
}

export async function submeterParaRevisao(studentId) {
  const { data } = await api.patch(`/students/${studentId}/submit-for-review`, {})
  return data
}

export async function listarPendentesRevisao() {
  const { data } = await api.get('/students/review/pending')
  return data
}

export async function listarPorReviewStatus(reviewStatus) {
  const { data } = await api.get(`/students/review/status/${encodeURIComponent(reviewStatus)}`)
  return data
}

// Timeout maior que o padrão (10s) — aprovar agora também gera as
// mensalidades do restante do ano pro aluno (backend), o que pode levar
// mais tempo que uma requisição comum e disparar falso-timeout no cliente
// mesmo com o backend concluindo com sucesso.
const REVIEW_TIMEOUT_MS = 30_000

export async function aprovarCadastro(studentId) {
  const { data } = await api.patch(`/students/${studentId}/review`,
      { action: 'APPROVE', notes: null }, { timeout: REVIEW_TIMEOUT_MS })
  return data
}

export async function rejeitarCadastro(studentId, notes) {
  const { data } = await api.patch(`/students/${studentId}/review`,
      { action: 'REJECT', notes }, { timeout: REVIEW_TIMEOUT_MS })
  return data
}

export async function retornarCadastro(studentId, notes) {
  const { data } = await api.patch(`/students/${studentId}/review`,
      { action: 'RETURN', notes }, { timeout: REVIEW_TIMEOUT_MS })
  return data
}

export function clearUserCache(uid = null) {
  if (uid) {
    cacheInvalidate(`user:${uid}`)
    invalidatePrefix(`notifications:`)
    invalidatePrefix(`person:`)
    invalidatePrefix(`student:`)
    invalidatePrefix(`address:`)
    invalidatePrefix(`routes:`)
  } else {
    memClear()
  }
}

// Invalida o cache de definições de rota após criar/editar/arquivar rota.
export function clearRoutesCache() {
  invalidatePrefix(`routes:`)
  invalidatePrefix(`route:`)
}
